import axios from 'axios'
import intl from 'react-intl-universal'
import { call, put, select, takeEvery, takeLatest, delay } from 'redux-saga/effects'
import { PAM_API, PARTICIPANT_API, SETTINGS_API } from 'constants/url'
import { fromIdentity } from 'store/selectors'
import { toast } from 'react-toastify'
import * as actions from './actions'
import { getCurrentRetailer, getLinkExport, getLinkImport, getLinkImportSame } from './selectors'

export function* getProductsList({ payload }) {
  try {
    const ticket = yield select(fromIdentity.getTicket)
    const params = { ...payload }
    delete params.deleteChecked
    if (params.brand === 'All') delete params.brand
    if (params.participant_id === 'all') delete params.participant_id

    const { data } = yield call(axios.get, `${PAM_API}/api/v2/products`, {
      params: {
        page_size: 10,
        page_number: 1,
        ...params
      },
      headers: {
        authenticateit_identity_ticket: ticket
      }
    })

    yield put(actions.pamGetProductsListSuccess(data))

    if (payload.deleteChecked) {
      yield put(actions.pamSetCheckedRetailer(payload.participant_id))
    }
  } catch (error) {
    yield put(actions.pamGetProductsListFail(error))
  }
}

export function* getRetailersList({ payload }) {
  try {
    const ticket = yield select(fromIdentity.getTicket)

    const { data } = yield call(axios.get, `${PAM_API}/api/retailers`, {
      params: {
        ...payload
      },
      headers: {
        authenticateit_identity_ticket: ticket
      }
    })

    yield put(
      actions.pamGetRetailersListSuccess(
        data &&
          data.length &&
          data.map(retailer => ({
            ...retailer,
            name: (retailer && retailer.name && retailer.name.replace(/\\/g, '')) || ''
          }))
      ) || []
    )
  } catch (error) {
    yield put(actions.pamGetRetailersListFail(error))
  }
}

export function* getRetailersListV2({ payload }) {
  try {
    const ticket = yield select(fromIdentity.getTicket)

    const { data } = yield call(axios.get, `${PAM_API}/api/v2/retailers`, {
      params: {
        ...payload
      },
      headers: {
        authenticateit_identity_ticket: ticket
      }
    })

    yield put(
      actions.pamGetRetailersListV2Success(
        data &&
          data.rows &&
          data.rows.length &&
          data.rows.map(retailer => ({
            ...retailer,
            name: (retailer && retailer.name && retailer.name.replace(/\\/g, '')) || '-'
          }))
      ) || []
    )
  } catch (error) {
    console.log(error)
    yield put(actions.pamGetRetailersListV2Fail(error))
  }
}

export function* getOCRRetailersList({ payload }) {
  try {
    const ticket = yield select(fromIdentity.getTicket)

    const { data } = yield call(axios.get, `${SETTINGS_API}/retailer_ocr_ids`, {
      params: {
        ...payload
      },
      headers: {
        authenticateit_identity_ticket: ticket
      }
    })

    yield put(
      actions.pamGetOCRRetailersListSuccess(
        data.ocr_retailers &&
          data.ocr_retailers.length &&
          data.ocr_retailers.map(retailer => ({
            ...retailer,
            name: (retailer && retailer.name && retailer.name.replace(/\\/g, '')) || ''
          }))
      ) || []
    )
  } catch (error) {
    yield put(actions.pamGetOCRRetailersListFail(error))
  }
}

export function* getPrices({ payload }) {
  try {
    const ticket = yield select(fromIdentity.getTicket)

    const { data } = yield call(axios.get, `${PAM_API}/api/v2/prices`, {
      params: {
        product_id: payload
      },
      headers: {
        authenticateit_identity_ticket: ticket
      }
    })

    yield put(actions.pamGetPricesSuccess(data))
  } catch (error) {
    yield put(actions.pamGetPricesFail(error))
  }
}

export function* getSameProducts({ payload }) {
  try {
    const ticket = yield select(fromIdentity.getTicket)
    let result = []

    if (payload && payload.gtin) {
      const { data } = yield call(axios.get, `${PAM_API}/api/similar`, {
        params: {
          gtin: payload.gtin
        },
        headers: {
          authenticateit_identity_ticket: ticket
        }
      })

      result = [...data]
    }

    yield put(actions.pamGetSameProductsSuccess(result))
  } catch (error) {
    yield put(actions.pamGetSameProductsFail(error))
  }
}

export function* addProduct({ payload }) {
  try {
    const ticket = yield select(fromIdentity.getTicket)
    const product = {
      ...payload,
      spg_id: payload.spg_name,
      pvg_id: payload.pvg_gtin
    }
    delete product.spg_name
    delete product.pvg_gtin
    const { data } = yield call(axios.post, `${PAM_API}/api/v2/products`, product, {
      headers: {
        authenticateit_identity_ticket: ticket
      }
    })

    if ((data && data.result === 'Product added') || data === 'OK') {
      toast.success(intl.get('alerts.addProductSuccess', { name: payload.name }))
      yield put(actions.pamAddProductSuccess())
      yield put(actions.pamGetProductsList({ participant_id: payload.participant_id }))

      return
    }

    toast.error(intl.get('alerts.addProductFail'))
    yield put(actions.pamAddProductFail(false))
  } catch (error) {
    if (error.response && error.response.data && error.response.data.message) {
      toast.error(`${error.response.data.message}`)
    } else {
      toast.error(intl.get('alerts.addProductFail'))
    }

    yield put(actions.pamAddProductFail(false))
  }
}

export function* updateProduct({ payload }) {
  try {
    console.log(payload)
    const ticket = yield select(fromIdentity.getTicket)
    const product = {
      ...payload,
      spg_id: payload.spg_name,
      pvg_id: payload.pvg_gtin
    }
    delete product.spg_name
    delete product.pvg_gtin
    const { data } = yield call(axios.patch, `${PAM_API}/api/v2/products`, product, {
      headers: {
        authenticateit_identity_ticket: ticket
      }
    })

    if (data.result === 'Product updated' || data === 'OK') {
      toast.success(intl.get('alerts.updateProductSuccess', { name: payload.name.value }))
      yield put(actions.pamUpdateProductSuccess())
      yield put(actions.pamGetProductsList({ participant_id: payload.participant_id.value }))

      return
    }

    toast.error(intl.get('alerts.updateProductFail'))
    yield put(actions.pamUpdateProductFail(false))
  } catch (error) {
    if (error.response && error.response.data && error.response.data.message) {
      toast.error(`${error.response.data.message}`)
    } else {
      toast.error(intl.get('alerts.updateProductFail'))
    }

    yield put(actions.pamUpdateProductFail(false))
  }
}

export function* deleteProduct({ payload }) {
  try {
    const ticket = yield select(fromIdentity.getTicket)

    const { data } = yield call(axios.delete, `${PAM_API}/api/products`, {
      headers: {
        authenticateit_identity_ticket: ticket
      },
      data: {
        id: payload.id
      }
    })

    if (data.result === 'Product deleted') {
      toast.success(intl.get('alerts.deleteProductSuccess', { name: payload.name }))
      yield put(actions.pamDeleteProductSuccess())
      yield put(actions.pamGetProductsList({ participant_id: payload.participant_id }))

      return
    }

    toast.error(intl.get('alerts.deleteProductFail'))
    yield put(actions.pamDeleteProductFail())
  } catch (error) {
    if (error.response && error.response.data && error.response.data.error) {
      toast.error(`${error.response.data.error}`)
    }
    toast.error(intl.get('alerts.deleteProductFail'))
    yield put(actions.pamDeleteProductFail(error))
  }
}

export function* deleteSimilar({ payload }) {
  try {
    const ticket = yield select(fromIdentity.getTicket)

    const { data } = yield call(axios.delete, `${PAM_API}/api/similar/delete`, {
      headers: {
        authenticateit_identity_ticket: ticket
      },
      data: {
        product_gtin: payload.product_gtin,
        gtin: payload.gtin
      }
    })

    if (data.success) {
      toast.success(intl.get('alerts.deleteProductSuccess', { name: data.message }))
      yield put(actions.pamDeleteProductSuccess())
      yield put(actions.pamGetSameProducts({ gtin: payload.gtin }))
      return
    }

    toast.error(intl.get('alerts.deleteProductFail'))
    yield put(actions.pamDeleteProductFail())
  } catch (error) {
    if (error.response && error.response.data && error.response.data.error) {
      toast.error(`${error.response.data.error}`)
    }
    toast.error(intl.get('alerts.deleteProductFail'))
    yield put(actions.pamDeleteProductFail(error))
  }
}

export function* updateSameProducts({ payload }) {
  try {
    const ticket = yield select(fromIdentity.getTicket)

    const { data } = yield call(
      axios.post,
      `${PAM_API}/api/similar/create`,
      {
        product_gtin: payload.product_gtin,
        gtin: payload.gtin
      },
      {
        headers: {
          authenticateit_identity_ticket: ticket
        }
      }
    )

    if (data.success) {
      toast.success(
        intl.get('alerts.addSameProductSuccess', {
          name: data.message
        }),
        {
          name: data.message
        }
      )
      yield put(actions.pamUpdateSameProductSuccess())
      yield put(actions.pamGetSameProducts({ gtin: payload.htin, product_gtin: payload.product_gtin }))

      return
    }
    toast.error(intl.get('alerts.addSameProductFail'))
  } catch (error) {
    if (error.response && error.response.data && error.response.data.message) {
      toast.error(`${error.response.data.message}`)
    } else {
      toast.error(intl.get('alerts.addSameProductFail'))
    }

    yield put(actions.pamUpdateSameProductFail(error))
  }
}

export function* updatePrice({ payload }) {
  try {
    const ticket = yield select(fromIdentity.getTicket)
    const params = { ...payload }
    const idProduct = params.product_id
    delete params.product_id

    const { data } = yield call(axios.patch, `${PAM_API}/api/prices`, params, {
      headers: {
        authenticateit_identity_ticket: ticket
      }
    })

    if (data.result === 'Price updated') {
      toast.success(intl.get('alerts.updatePriceSuccess', { name: payload.label }))
      yield put(actions.pamUpdatePriceSuccess())
      yield put(actions.pamGetPrices(idProduct))

      return
    }

    toast.success(intl.get('alerts.updatePriceFailed', { name: payload.label }))
    yield put(actions.pamUpdatePriceFail())
  } catch (error) {
    if (error.response && error.response.data && error.response.data.error) {
      toast.error(`${error.response.data.error}`)
    } else {
      toast.success(intl.get('alerts.updatePriceFailed', { name: payload.label }))
    }

    yield put(actions.pamUpdatePriceFail(error))
  }
}

export function* searchGtin({ payload }) {
  yield delay(300)
  try {
    const ticket = yield select(fromIdentity.getTicket)
    const { data } = yield call(
      axios.post,
      `${PAM_API}/api/gtins/search`,
      {
        search_string: payload,
        limit: 10
      },
      {
        headers: {
          authenticateit_identity_ticket: ticket,
          'Content-Type': 'application/json'
        }
      }
    )

    yield put(actions.pamSearchGtinSuccess(data))
  } catch (error) {
    yield put(actions.pamSearchGtinFail(error))
  }
}

export function* getParticipants({ payload }) {
  yield delay(300)
  try {
    const ticket = yield select(fromIdentity.getTicket)
    const { data } = yield call(axios.get, `${PAM_API}/api/participants`, {
      params: {
        participant_name: payload,
        limit: 10
      },
      headers: {
        authenticateit_identity_ticket: ticket,
        'Content-Type': 'application/json'
      }
    })

    yield put(
      actions.pamGetParticipantsSuccess(
        data.map(({ id, name }) => ({ label: (name && name.replace(/\\/g, '')) || '', value: id }))
      )
    )
  } catch (error) {
    yield put(actions.pamGetParticipantsFail(error))
  }
}

export function* getRetailerBrands({ payload }) {
  try {
    const ticket = yield select(fromIdentity.getTicket)
    const { data } = yield call(axios.post, `${PARTICIPANT_API}/v2/brands/global/list`, payload, {
      headers: { authenticateit_identity_ticket: ticket, 'Content-Type': 'application/json' }
    })
    if (data?.data) {
      yield put(actions.pamGetRetailersBrandsSuccess(data.data))
    }
  } catch (error) {
    yield put(actions.pamGetRetailersBrandsFail(error))
  }
}

export function* exportProducts({ payload }) {
  try {
    const ticket = yield select(fromIdentity.getTicket)
    const { data } = yield call(axios.post, `${PAM_API}/api/export/products`, payload, {
      headers: {
        authenticateit_identity_ticket: ticket
      }
    })
    yield put(actions.pamExportProductsSuccess(data.result))
    toast.success(intl.get('alerts.exportSuccess'))
    let downloadLink = yield select(({ pam }) => pam.downloadLink)
    let exportLink = yield select(({ pam }) => pam.exportLink)
    while (downloadLink === null || downloadLink === undefined) {
      yield delay(3000)
      downloadLink = yield select(({ pam }) => pam.downloadLink)
      exportLink = yield select(({ pam }) => pam.exportLink)
      if (exportLink !== 'cancel') yield put(actions.pamGetExportLink())
      else yield put(actions.setItemValue({ name: 'exportLink', value: null }))
    }
  } catch (error) {
    if (error.response && error.response.data && error.response.data.error) {
      toast.error(`${error.response.data.error}`)
    }
    yield put(actions.pamExportProductsFail(error))
  }
}

export function* getExportLink() {
  try {
    const ticket = yield select(fromIdentity.getTicket)
    const link = yield select(getLinkExport)

    let downloadLink

    if (link) {
      const { data } = yield call(axios.get, `${PAM_API}/api/export/link`, {
        params: {
          link_id: link
        },
        headers: {
          authenticateit_identity_ticket: ticket
        }
      })

      downloadLink = data.result
    }

    if (downloadLink !== null && downloadLink !== 'in progress') {
      yield put(actions.pamGetExportLinkSuccess(downloadLink))
    }
  } catch (error) {
    if (error.response && error.response.data && error.response.data.error) {
      toast.error(`${error.response.data.error}`)
    }
    yield put(actions.pamGetExportLinkFail(error))
  }
}

export function* getImportStatus() {
  try {
    const ticket = yield select(fromIdentity.getTicket)
    const id = yield select(getLinkImport)
    const idSame = yield select(getLinkImportSame)

    const result = {
      import: null,
      importSame: null
    }

    if (id) {
      const { data } = yield call(axios.get, `${PAM_API}/api/import/status`, {
        params: {
          id
        },
        headers: {
          authenticateit_identity_ticket: ticket
        }
      })

      result.import = data.result && (data.result.includes('completed') || data.result.includes('aborted')) ? null : id

      if (data.result && data.result.includes('aborted')) {
        toast.error(intl.get('alerts.importAborted'))
      } else if (data.result && data.result.includes('completed')) {
        toast.success(intl.get('alerts.importCompleted'))
        const currentRetailer = yield select(getCurrentRetailer)
        yield put(actions.pamGetProductsList({ participant_id: currentRetailer }))
      }
    }

    if (idSame) {
      const { data } = yield call(axios.get, `${PAM_API}/api/import/status`, {
        params: {
          id: idSame
        },
        headers: {
          authenticateit_identity_ticket: ticket
        }
      })

      result.importSame =
        data.result && (data.result.includes('completed') || data.result.includes('aborted')) ? null : idSame
      if (data.result && data.result.includes('aborted')) {
        toast.error(intl.get('alerts.importAborted'))
      } else if (data.result && data.result.includes('completed')) {
        toast.success(intl.get('alerts.importCompleted'))
      }
    }

    yield put(actions.pamGetImportStatusSuccess(result))
  } catch (error) {
    if (error.response && error.response.data && error.response.data.error) {
      toast.error(`${error.response.data.error}`)
    }
    yield put(actions.pamGetImportStatusFail(error))
  }
}

export function* importProducts({ payload }) {
  try {
    const ticket = yield select(fromIdentity.getTicket)

    const { data } = yield call(axios.post, `${PAM_API}/api/import/products`, payload, {
      headers: {
        authenticateit_identity_ticket: ticket
      }
    })

    const link = !data || !data.link ? null : data.link
    yield put(actions.pamImportProductsSuccess(link))
    toast.success(intl.get('alerts.importSuccess'))
  } catch (error) {
    if (error.response && error.response.data && error.response.data.message) {
      toast.error(`${error.response.data.message}`)
    } else {
      toast.error(intl.get('alerts.importFailed'))
    }
    yield put(actions.pamImportProductsFail(error))
  }
}

export function* importSameProducts({ payload }) {
  try {
    const ticket = yield select(fromIdentity.getTicket)
    const params = new FormData()
    params.append('data', payload)

    const { data } = yield call(axios.post, `${PAM_API}/api/spg/tools/import`, params, {
      headers: {
        authenticateit_identity_ticket: ticket,
        'Content-Type': false
      }
    })

    yield put(actions.pamImportSameProductsSuccess(data.link))
    toast.success(intl.get('alerts.importSuccess'))
  } catch (error) {
    if (error.response && error.response.data && error.response.data.message) {
      toast.error(`${error.response.data.message}`)
    } else {
      toast.error(intl.get('alerts.importFailed'))
    }
    yield put(actions.pamImportSameProductsFail(error))
  }
}

export function* addPrice({ payload }) {
  try {
    const ticket = yield select(fromIdentity.getTicket)

    const { data } = yield call(
      axios.patch,
      `${PAM_API}/api/products`,
      {
        product_price: {
          value: payload.price,
          delete: false
        },
        product_id: payload.product_id
      },
      {
        headers: {
          authenticateit_identity_ticket: ticket
        }
      }
    )

    if (data.result === 'Product updated') {
      yield put(actions.pamUpdateSameProductSuccess())
      const currentRetailer = yield select(getCurrentRetailer)
      toast.success(intl.get(`alerts.addPriceSuccess`))
      yield put(actions.pamGetProductsList({ participant_id: currentRetailer }))
      yield put(actions.pamGetPrices(payload.product_id.value))

      return
    }

    toast.error(intl.get(`alerts.addPriceFail`))
    yield put(actions.pamUpdateSameProductFail())
  } catch (error) {
    if (error.response && error.response.data && error.response.data.message) {
      toast.error(`${error.response.data.message}`)
    } else {
      toast.error(intl.get(`alerts.addPriceFail`))
    }

    yield put(actions.pamUpdateSameProductFail(error))
  }
}

export function* searchStoreId({ payload }) {
  yield delay(300)
  try {
    const ticket = yield select(fromIdentity.getTicket)
    const { data } = yield call(axios.get, `${PAM_API}/api/retailers/store_id`, {
      params: {
        store_id: payload,
        limit: 10
      },
      headers: {
        authenticateit_identity_ticket: ticket,
        'Content-Type': 'application/json'
      }
    })

    yield put(actions.pamSearchStoreIdSuccess(data))
  } catch (error) {
    yield put(actions.pamSearchStoreIdFail(error))
  }
}

export function* getSPGList({ payload }) {
  try {
    const ticket = yield select(fromIdentity.getTicket)
    const params = { ...payload }
    // delete params.deleteChecked
    // if (params.brand === 'All') delete params.brand
    // if (params.participant_id === 'all') delete params.participant_id

    const { data } = yield call(axios.get, `${PAM_API}/api/spg`, {
      params: {
        // page_size: 10,
        // page_number: 1,
        ...params
      },
      headers: {
        authenticateit_identity_ticket: ticket
      }
    })
    yield put(actions.pamGetSPGListSuccess(data))
  } catch (error) {
    yield put(actions.pamGetSPGListFail(error))
  }
}

export default function*() {
  yield takeEvery(actions.GET_PRODUCTS_LIST, getProductsList)
  yield takeEvery(actions.GET_RETAILERS_LIST, getRetailersList)
  yield takeEvery(actions.GET_RETAILERS_LIST_V2, getRetailersListV2)
  yield takeEvery(actions.GET_OCR_RETAILERS_LIST, getOCRRetailersList)
  yield takeEvery(actions.GET_SAME_PRODUCTS, getSameProducts)
  yield takeEvery(actions.DELETE_SIMILAR, deleteSimilar)
  yield takeEvery(actions.GET_PRICES, getPrices)
  yield takeEvery(actions.UPDATE_PRODUCT, updateProduct)
  yield takeEvery(actions.ADD_PRODUCT, addProduct)
  yield takeEvery(actions.UPDATE_SAME_PRODUCT, updateSameProducts)
  yield takeLatest(actions.SEARCH_GTIN, searchGtin)
  yield takeEvery(actions.DELETE_PRODUCT, deleteProduct)
  yield takeEvery(actions.GET_RETAILER_BRANDS, getRetailerBrands)
  yield takeEvery(actions.UPDATE_PRICE, updatePrice)
  yield takeLatest(actions.GET_PARTICIPANTS, getParticipants)
  yield takeEvery(actions.EXPORT_PRODUCTS, exportProducts)
  yield takeEvery(actions.GET_EXPORT_LINK, getExportLink)
  yield takeEvery(actions.IMPORT_PRODUCTS, importProducts)
  yield takeEvery(actions.GET_IMPORT_STATUS, getImportStatus)
  yield takeEvery(actions.IMPORT_SAME_PRODUCTS, importSameProducts)
  yield takeEvery(actions.ADD_PRICE, addPrice)
  yield takeLatest(actions.SEARCH_STORE_ID, searchStoreId)
  yield takeEvery(actions.GET_SPG_LIST, getSPGList)
}
